import { type Common } from './common.js';
import {
  type BalanceTransaction,
  type IngestPaymentBalance,
  type PaymentBalance,
} from './models/payment/balance.js';
import {
  type Payment,
  type PaymentBalancesInfo,
  type PaymentDashboardQuery,
  type PaymentDashboardResponse,
  type PaymentEvent,
  type PaymentInfo,
  type PaymentType,
  type SetupFutureUsageValues,
} from './models/payment/payment.js';

export class API {
  common: Common;
  baseEndpoint = '/patient-previsit/encounter/payments';

  constructor(common: Common) {
    this.common = common;
  }

  async findOneByEncounterId(encounterId: string): Promise<Payment> {
    const url = this.common.constructPath(`/encounters/${encounterId}/payment`);
    return await this.common.getJSON(url);
  }

  // administrative endpoints
  async createPaymentForEncounter(
    encounterId: string,
    amount: number,
    currency: string,
    previousBalance?: number,
    visitFeeAmount?: number,
    balances?: Omit<IngestPaymentBalance, 'billingType' | 'location' | 'balanceDetails'>[]
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`/previsit/encounters/payments`);
    return await this.common.postJSON(url, {
      encounterId,
      amount,
      currency,
      ...(previousBalance !== undefined && { previousBalance }),
      ...(visitFeeAmount !== undefined && { visitFeeAmount }),
      balances,
    });
  }

  async resolvePaymentForEncounters(
    encounterIds: string[]
  ): Promise<{ captured: string[]; synced: string[]; errored: string[] }> {
    const url = this.common.constructPath(`/previsit/encounters/payments/resolve`);
    return await this.common.postJSON(url, { encounterIds });
  }

  async bulkCancelPaymentsById(
    encounterPaymentIds: string[]
  ): Promise<{ succeeded: string[]; failed: string[] }> {
    const url = this.common.constructPath(`/previsit/encounters/payments/cancel`);
    return await this.common.postJSON(url, { encounterPaymentIds });
  }

  async bulkRefundPaymentsById(
    encounterPaymentIds: string[]
  ): Promise<{ succeeded: string[]; failed: string[] }> {
    const url = this.common.constructPath(`/previsit/encounters/payments/refund`);
    return await this.common.postJSON(url, { encounterPaymentIds });
  }

  // patient app endpoints
  async fetchPaymentInfo(queryParams?: {
    authenticationCode: string;
    previsitFormSetId: string;
  }): Promise<PaymentInfo> {
    const { authenticationCode, previsitFormSetId } = queryParams ?? {};
    const url = this.common.constructPath(`${this.baseEndpoint}`, {
      previsitFormSetId,
      authenticationCode,
    });
    return await this.common.getJSON(url);
  }

  async fetchPaymentInfoByPaymentId(paymentId: string): Promise<PaymentInfo> {
    const url = this.common.constructPath(`/payments/${paymentId}/info`);
    return await this.common.getJSON(url);
  }

  async fetchPaymentBalances(queryParams?: {
    authenticationCode: string;
    previsitFormSetId: string;
  }): Promise<PaymentBalancesInfo> {
    const { authenticationCode, previsitFormSetId } = queryParams ?? {};
    const url = this.common.constructPath(`${this.baseEndpoint}/balances`, {
      previsitFormSetId,
      authenticationCode,
    });
    return await this.common.getJSON(url);
  }

  async refreshPaymentStatus(
    authenticationCode: string,
    previsitFormSetId: string
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/refresh`, {
      previsitFormSetId,
      authenticationCode,
    });
    return await this.common.postJSON(url, {});
  }

  async refreshPayment(paymentId: string): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`/payments/${paymentId}/refresh`);
    return await this.common.postJSON(url, {});
  }

  async finishPaymentWithSavedMethod({
    authenticationCode,
    previsitFormSetId,
    methodIndex,
    sendReceipt = false,
  }: {
    authenticationCode: string;
    previsitFormSetId: string;
    methodIndex: number;
    sendReceipt?: boolean;
  }): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/use-saved`, {
      authenticationCode,
      previsitFormSetId,
    });
    return await this.common.postJSON(url, {
      selectedPaymentMethod: methodIndex,
      sendReceipt,
    });
  }

  async updatePaymentReceiptRequest({
    authenticationCode,
    previsitFormSetId,
    sendReceipt,
    email,
  }: {
    authenticationCode: string;
    previsitFormSetId: string;
    sendReceipt: boolean;
    email?: string;
  }): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/request-receipt`, {
      authenticationCode,
      previsitFormSetId,
    });
    return await this.common.postJSON(url, { sendReceipt, email });
  }

  async updatePaymentAmount({
    authenticationCode,
    previsitFormSetId,
    paymentType,
    partialBalanceAmount,
  }: {
    authenticationCode: string;
    previsitFormSetId: string;
    paymentType: PaymentType;
    partialBalanceAmount: number | null;
  }): Promise<PaymentInfo> {
    const url = this.common.constructPath(`${this.baseEndpoint}/adjust-payment`, {
      authenticationCode,
      previsitFormSetId,
    });
    return await this.common.postJSON(url, {
      paymentType,
      partialBalanceAmount,
    });
  }

  async updatePayment(paymentId: string, paymentType: PaymentType): Promise<PaymentInfo> {
    const url = this.common.constructPath(`/payments/${paymentId}/adjust-payment`);
    return await this.common.postJSON(url, { paymentType });
  }

  async upsertPayment(args: {
    authenticationCode: string;
    previsitFormSetId: string;
    balanceTransactions: BalanceTransaction[];
    setupFutureUsage?: SetupFutureUsageValues;
  }): Promise<PaymentInfo> {
    const { authenticationCode, previsitFormSetId, balanceTransactions, setupFutureUsage } = args;
    const url = this.common.constructPath(`${this.baseEndpoint}/upsert-payment`, {
      authenticationCode,
      previsitFormSetId,
    });
    return await this.common.postJSON(url, {
      balanceTransactions,
      ...(setupFutureUsage !== undefined && { setupFutureUsage }),
    });
  }

  async updatePatientSkipFeedback({
    authenticationCode,
    previsitFormSetId,
    feedback,
  }: {
    authenticationCode: string;
    previsitFormSetId: string;
    feedback: string;
  }) {
    const url = this.common.constructPath(`${this.baseEndpoint}/feedback`, {
      authenticationCode,
      previsitFormSetId,
    });
    return await this.common.postJSON(url, { feedback });
  }

  // staff web app endpoints
  async getDashboardPayments(query: PaymentDashboardQuery): Promise<PaymentDashboardResponse> {
    const url = this.common.constructPath('/previsit/encounters/payments/dashboard', query);
    return await this.common.getJSON(url);
  }

  async downloadPaymentReport(): Promise<void> {
    const url = this.common.constructPath('/previsit/encounters/payments/dashboard/download');
    return await this.common.getJSON(url);
  }

  async getReconReportForPayoutId(args: {
    payoutId: string;
    includeFailedRefunds?: boolean;
  }): Promise<{ rows: string[][]; header: string[] }> {
    const { payoutId, includeFailedRefunds } = args;
    const url = this.common.constructPath(
      `/previsit/encounters/payments/payout-reconciliation/${payoutId}`,
      { csv: false, includeFailedRefunds }
    );
    return await this.common.getJSON(url);
  }

  async getDashboardPaymentEvents(paymentId: string): Promise<PaymentEvent[]> {
    const url = this.common.constructPath(
      `/previsit/encounters/payments/dashboard/${paymentId}/events`
    );
    return await this.common.getJSON(url);
  }

  async getPaymentRefundAmount(paymentId: string): Promise<number> {
    const url = this.common.constructPath(
      `/previsit/encounters/payments/dashboard/${paymentId}/refund-amount`
    );
    return await this.common.getJSON(url);
  }

  async upsertEncounterPaymentCopay(values: {
    visitFeeAmount: number;
    encounterId: string;
  }): Promise<Payment> {
    const url = this.common.constructPath(`/payments`);
    const { updatedPayment } = await this.common.patchJSON(url, values);
    return updatedPayment;
  }
  async sendPaymentReceipt({
    emailRecipients,
    paymentId,
  }: {
    paymentId: string;
    emailRecipients: string[];
  }): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`/payments/${paymentId}/send-receipt`);

    return await this.common.postJSON(url, { emailRecipients });
  }

  // admin endpoints for balance management
  async getPaymentBalances(args: {
    encounterId: string;
    includeDeleted?: boolean;
    includeWithoutPayment?: boolean;
  }): Promise<PaymentBalance[]> {
    const { encounterId, includeDeleted, includeWithoutPayment } = args;
    const url = this.common.constructPath(`/encounter/${encounterId}/payment/balance`, {
      includeDeleted,
      includeWithoutPayment,
    });
    return await this.common.getJSON(url);
  }

  async updatePaymentBalance(
    encounterId: string,
    balanceId: string,
    body: {
      deleted?: null;
      encounterPaymentId?: string | null;
      exportStatus?: string | null;
      exportCompletedTimestamp?: Date | null;
    }
  ): Promise<PaymentBalance> {
    const url = this.common.constructPath(
      `/encounters/${encounterId}/payment/balance/${balanceId}`
    );
    return await this.common.patchJSON(url, body);
  }

  async deletePaymentBalance(
    encounterId: string,
    paymentId: string,
    paymentBalanceId: string
  ): Promise<void> {
    const url = this.common.constructPath(`/encounter/${encounterId}/payment/balance/delete`);
    return await this.common.postJSON(url, {
      paymentId,
      paymentBalanceId,
    });
  }
}
